import {
  Channel,
  Keypad,
  NativeBridgeError,
  NativeBridgeResponse /** , deviceUtils*/,
  NativeBridgeService,
} from 'scbk-services';

import { SecureKeypadType } from '../enums/SecureKeypadTypeEnum';

// import { scriptUtils } from 'scbk-shared-module/utils';

import { Dialog } from 'scbk-shared-module/modules/GlobalComponents';
import {
  SecureKeypadContext,
  SecureKeypadDriver,
  SecureKeypadFieldId,
  SecureKeypadFieldOption,
} from '../types/types';

export class NativeKeypadDriver implements SecureKeypadDriver {
  // private static instance: NativeKeypadDriver | null = null;

  private initialized = false;

  private optionRegistry = new Map<SecureKeypadFieldId, SecureKeypadFieldOption>();

  private elementRegistry = new Map<SecureKeypadFieldId, HTMLElement>();

  readonly id = 'native';

  constructor() {}

  /**
   * 지원여부
   * @param ctx 컨텍스트
   * @returns
   */
  supported(ctx: SecureKeypadContext): boolean {
    // 앱 환경에서만
    return ctx.channel === Channel.MB;
  }

  /**
   * 초기화 수행
   * @param ctx
   */
  init(ctx: SecureKeypadContext) {
    console.log('# NativeKeypadDriver init ctx');

    if (typeof window === 'undefined') {
      return;
    }

    this.initOnce(ctx);
  }

  destroy() {
    this.initialized = false;
    this.optionRegistry.clear();
    this.elementRegistry.clear();
  }

  private clickHandlers = new WeakMap<HTMLElement, EventListener>();

  /**
   * 키패드 등록
   * @param el 입력요소
   * @param option 키패드 옵션
   * @returns
   */
  register(
    el: HTMLInputElement,
    option?: SecureKeypadFieldOption,
    setScroll?: React.Dispatch<React.SetStateAction<boolean>>,
    setPopupScroll?: React.Dispatch<React.SetStateAction<boolean>>,
    scrollLock?: boolean,
  ) {
    if (!el) {
      return;
    }

    const _opt = this.mergeOptions(el.id, option);
    if (!_opt) {
      return;
    }

    this.optionRegistry.set(_opt.fieldId, _opt);

    el.setAttribute('readonly', 'true');
    el.setAttribute('inputmode', 'none');

    // disabled 이어도 흰색배경 적용
    el.classList.add('not-disabled');

    let type = SecureKeypadType.Qwerty; //기본 설정
    const dataEnc = el.getAttribute('data-enc');
    if (dataEnc === 'num') {
      type = SecureKeypadType.Num;
    }

    const aesEnc = el.getAttribute('data-aesenc') ?? '0'; // 기본은 0이고, 솔루션 연동 값인경우 1로 셋팅 (예:인증서 암호 등 연동)

    const serverKey = el.getAttribute('data-nf-public-key') ?? '';

    if (aesEnc !== '3') {
      el.setAttribute('type', 'password');
    }

    const oldHandler = this.clickHandlers.get(el);

    if (oldHandler) {
      console.log('### oldHander 삭제');
      el.removeEventListener('click', oldHandler);
    }

    const handleClickEvent = async (event: MouseEvent | TouchEvent) => {
      console.log('### keypad start ###');
      event.preventDefault();
      event.stopPropagation();

      const opening = el.getAttribute('data-keypad-opening');
      if (opening === 'Y') {
        console.log('### keypad 이중호출이다.');
        return;
      }
      el.setAttribute('data-keypad-opening', 'Y');

      //입력요소 값 초기화 처리
      el.value = '';

      //보안키패드 요청
      const request = {
        id: el.id,
        type,
        serverKey: encodeURIComponent(serverKey),
        maxlength: `${el.maxLength}`,
        aesEnc,
      };
      const nativeBridgeService: NativeBridgeService = NativeBridgeService.getInstance();

      const originScrollY = window.scrollY;

      try {
        await nativeBridgeService.callWithCallback('doNEncKeyPad', request, {
          callback: {
            once: false,
            fn: (response: NativeBridgeResponse<Keypad.EncKeypadResponse>) => {
              const { payload, status, error } = response;

              if (status === 'error' || status === 'fail') {
                const { code, message } = error as NativeBridgeError;
                Dialog.alert({
                  title: '오류',
                  content: `[${code}]${message}`,
                });
                return;
              }

              if (!payload) {
                Dialog.alert({
                  title: '오류',
                  content: `응답데이터가 존재하지 않습니다.`,
                });
                return;
              }

              const { result, dummyData, encData, enc2Data, aesEnc } = payload;

              el.value = dummyData;
              el.setAttribute('data-enc-data', '');
              el.dispatchEvent(new Event('change', { bubbles: true }));

              console.log('# options', _opt);

              console.log('### result : ', result);

              // 완료케이스 처리
              if (result === '0') {
                //TODO 키패드 닫기 처리 콜백 처리
                el.setAttribute(
                  'data-enc-data',
                  aesEnc == '0' || aesEnc == '3' ? decodeURIComponent(encData) : encData,
                );
                el.setAttribute('data-enc-data2', enc2Data);

                // const opt = this.optionRegistry.get(el.id);
                // 암호화 SecureContext에 데이터 저장
                _opt?.setEncData?.();
                //
                _opt?.onComplete?.({
                  fieldId: el.id,
                  encData: encData,
                  enc2Data: enc2Data,
                  length: dummyData.length,
                  dummyData: dummyData,
                });
                el?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));

                // 팝업인지 확인
                const scrollParent = el.closest('.dialog-viewport') as HTMLElement | null;
                if (scrollParent) {
                  !scrollLock && setPopupScroll?.(false);
                } else {
                  !scrollLock && setScroll?.(false);
                  window.scrollTo({ top: originScrollY, behavior: 'instant' });
                }

                el.setAttribute('data-keypad-opening', '');
              }
              // 취소케이스 처리
              else if (result === '-1') {
                //TODO 취소처리
                el.value = '';
                el.setAttribute('data-enc-data', '');
                el.setAttribute('data-enc-data2', '');
                // const opt = this.optionRegistry.get(el.id);
                // 암호화 SecureContext에 데이터 저장
                _opt?.setEncData?.();
                //
                _opt?.onClose?.({
                  fieldId: el.id,
                  encData: '',
                  enc2Data: '',
                  length: 0,
                  dummyData: '',
                });
                el?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
                // 팝업인지 확인
                const scrollParent = el.closest('.dialog-viewport') as HTMLElement | null;

                if (scrollParent) {
                  !scrollLock && setPopupScroll?.(false);
                } else {
                  !scrollLock && setScroll?.(false);
                  window.scrollTo({ top: originScrollY, behavior: 'instant' });
                }

                el.setAttribute('data-keypad-opening', '');
              }
            },
          },
        });
      } catch (error) {
        el.setAttribute('data-keypad-opening', '');
        console.error('### error :', error);
      } finally {
        const securityField = document.getElementById(
          `${el.id}-securityField`,
        ) as HTMLElement | null;

        const targetEl = securityField ?? el;

        const scrollParent = el.closest('.dialog-viewport') as HTMLElement | null;
        const rect = targetEl.getBoundingClientRect();

        if (scrollParent) {
          const parentRect = scrollParent.getBoundingClientRect();

          const targetY =
            scrollParent.scrollTop + (rect.top - parentRect.top) - parentRect.height * 0.25;

          !scrollLock && setPopupScroll?.(true);
          setTimeout(() => {
            scrollParent.scrollTo({
              top: Math.max(0, targetY),
              behavior: 'smooth',
            });
          }, 300);
        } else {
          const vh = window.visualViewport?.height || window.innerHeight;
          const safeArea = vh - vh * 0.6;
          const targetY = window.scrollY + rect.top - vh * 0.4;
          if (rect.bottom > safeArea && setScroll) {
            !scrollLock && setScroll(true);
            setTimeout(() => {
              window.scrollTo({
                top: Math.max(0, targetY),
                behavior: 'smooth',
              });
            }, 300);
          }
        }
      }
    };

    //이벤트 삭제/삭제 진행
    el.removeEventListener('click', handleClickEvent);
    el.addEventListener('click', handleClickEvent);

    this.elementRegistry.set(el.id, el);
  }

  /**
   * 키패드 unregister
   * @param el
   */
  unregister(el: HTMLInputElement) {
    this.elementRegistry.delete(el.id);
  }

  /**
   * 키패드 open
   * @param fieldId 필드 ID
   * @returns
   */
  async open(fieldId: SecureKeypadFieldId): Promise<void> {
    const el = this.elementRegistry.get(fieldId);
    if (!el) {
      return;
    }

    el.click();
  }

  /**
   * 키패드를 닫는다.
   * @param fieldId 필드 ID
   */
  close(fieldId: SecureKeypadFieldId) {
    const el = this.elementRegistry.get(fieldId);
    if (!el) {
      return;
    }

    console.log('# close fieldId', fieldId);

    el.blur();
  }

  /**
   * 키패드 값  초기화
   */
  clear(fieldId?: SecureKeypadFieldId) {
    if (fieldId) {
      const el = this.elementRegistry.get(fieldId) as HTMLInputElement;
      if (!el) {
        return;
      }

      el.value = '';
    } else {
      const idsArr = Array.from(this.elementRegistry.keys());

      idsArr.map((key) => {
        const el = this.elementRegistry.get(key) as HTMLInputElement;
        if (!el) {
          return;
        }

        el.value = '';
      });
    }
  }

  /**
   * 암호화된 값 return
   */
  getEncData(): Promise<Record<string, string | Record<string, string>>> {
    return new Promise((resolve) => {
      const els = Array.from(this.elementRegistry.entries());

      const encMap = Object.fromEntries(
        els.map(([id, el]) => {
          const dataEnc = el.getAttribute('data-enc') === 'num' ? 'NUM' : 'STR';
          const encValue = `${dataEnc}|${el.getAttribute('data-enc-data') ?? ''}|${el.getAttribute('data-enc-data2') ?? ''}|${el.getAttribute('data-aes-enc') ?? '0'}`;
          return [id, encValue];
        }),
      );

      console.log('### encMap : ', encMap);

      const encData = {
        encType: 'nFilter', // TODO: Constants 화
        encMap: { ...encMap },
      };

      resolve(encData);
    });
  }

  /**
   *
   * @param fieldId
   * @returns
   */
  getOptions(fieldId: SecureKeypadFieldId): SecureKeypadFieldOption | undefined {
    return this.optionRegistry.get(fieldId as SecureKeypadFieldId);
  }

  forEachInput(callback: (el: HTMLElement) => void) {
    return this.elementRegistry.forEach(callback);
  }

  /**
   * 초기화를 한번만 수행한다.
   * @param ctx 컨텍스트
   * @returns
   */
  private initOnce(ctx: SecureKeypadContext) {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    const opts: SecureKeypadFieldOption[] = ctx.options ?? [];

    if (!opts) {
      console.warn('options is null or undefined');
    }

    if (opts) {
      opts.forEach((opt) => {
        this.optionRegistry.set(opt.fieldId, opt);
      });
    }

    console.log('### this.optionRegistry : ', this.optionRegistry);
  }

  /**
   * 초기 기본 옵션과 필드 등록시 전달된 옵션을 merge한다.
   * @param fieldId 필드 ID
   * @param option 필드 등록 시 전달된 옵션
   * @returns
   */
  private mergeOptions(
    fieldId: string,
    option?: SecureKeypadFieldOption,
  ): SecureKeypadFieldOption | undefined {
    const defaultOption = this.optionRegistry.get(fieldId);
    if (!option) {
      return defaultOption;
    }

    return {
      ...defaultOption,
      ...option,
    };
  }
}
